/**
 * CorrespondenceTimeline - Sprint Y Block E
 * 
 * Chronological vertical timeline of correspondence.
 * Groups by linked entity (mission, authorization) when viewing from entity detail.
 * 
 * HITL CRITICAL: Visually distinguishes PROPOSED vs CONFIRMED links.
 * Status indicators use lucide-react icons (Rule 19: NO emoji characters).
 * 
 * Icons:
 * - sent → Send
 * - received → Inbox
 * - deadline → Clock/AlarmClock
 * - approved → CheckCircle
 * - rejected → XCircle
 */

import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { 
  Send, Inbox, Clock, CheckCircle, XCircle, AlertTriangle,
  Mail, Paperclip, ChevronDown, ChevronUp, AlertCircle,
  Calendar, FileText, User, Plane, Shield
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../ui/collapsible";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";

// Status icon mapping - lucide-react only (Rule 19)
const STATUS_ICONS = {
  pending_review: Clock,
  reviewed: CheckCircle,
  linked: CheckCircle,
  archived: CheckCircle,
};

// Direction icon mapping
const DIRECTION_ICONS = {
  inbound: Inbox,
  outbound: Send,
};

// Source category icons
const SOURCE_ICONS = {
  regulator: Shield,
  atc: Send,
  operator_authority: FileText,
  police: AlertTriangle,
  fire: AlertTriangle,
  energy: AlertCircle,
  insurance: FileText,
  other: Mail,
};

// Source category colors
const SOURCE_COLORS = {
  regulator: "bg-blue-500",
  atc: "bg-purple-500",
  operator_authority: "bg-teal-500",
  police: "bg-red-500",
  fire: "bg-orange-500",
  energy: "bg-yellow-500",
  insurance: "bg-green-500",
  other: "bg-slate-500",
};

// Timeline dot colors based on status
const STATUS_DOT_COLORS = {
  pending_review: "bg-amber-500",
  reviewed: "bg-blue-500",
  linked: "bg-green-500",
  archived: "bg-slate-400",
};

export const CorrespondenceTimeline = ({ 
  correspondence = [], 
  onViewDetails,
  groupBy = null, // 'date' | 'entity' | null
  maxItems = null,
  showLoadMore = true,
}) => {
  const { translate: t, language } = useLanguage();
  const [expandedItems, setExpandedItems] = useState({});
  const [visibleCount, setVisibleCount] = useState(maxItems || 20);
  
  if (!correspondence || correspondence.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="correspondence-timeline-empty">
        <Mail className="w-12 h-12 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground">{t("correspondence.noCorrespondence")}</h3>
        <p className="text-sm text-muted-foreground/70 mt-1">{t("correspondence.noCorrespondenceDesc")}</p>
      </div>
    );
  }
  
  // Sort by received_at descending (most recent first)
  const sortedCorrespondence = [...correspondence].sort((a, b) => {
    const dateA = new Date(a.received_at || 0);
    const dateB = new Date(b.received_at || 0);
    return dateB - dateA;
  });
  
  // Limit visible items
  const visibleCorrespondence = sortedCorrespondence.slice(0, visibleCount);
  const hasMore = sortedCorrespondence.length > visibleCount;
  
  // Group by date helper
  const formatDateGroup = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (itemDate.getTime() === today.getTime()) return t("correspondence.today");
    if (itemDate.getTime() === yesterday.getTime()) return t("correspondence.yesterday");
    
    return date.toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };
  
  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString(language === "cs" ? "cs-CZ" : "en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  
  // Toggle item expansion
  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Group items by date if needed
  let groupedItems = visibleCorrespondence;
  let groups = null;
  
  if (groupBy === "date") {
    groups = {};
    visibleCorrespondence.forEach(item => {
      const dateKey = formatDateGroup(item.received_at);
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(item);
    });
  }
  
  // Render single timeline item
  const renderTimelineItem = (item, isLast = false) => {
    const {
      id,
      subject,
      sender_email,
      sender_name,
      received_at,
      direction = "inbound",
      status = "pending_review",
      source_category,
      source_name_cs,
      source_name_en,
      attachments = [],
      extracted = {},
      needs_review,
      proposed_authorization_id,
      proposed_mission_id,
      proposed_pilot_id,
      confirmed_authorization_id,
      confirmed_mission_id,
      confirmed_pilot_id,
    } = item;
    
    const DirectionIcon = DIRECTION_ICONS[direction] || Inbox;
    const SourceIcon = SOURCE_ICONS[source_category] || Mail;
    const sourceColor = SOURCE_COLORS[source_category] || "bg-slate-500";
    const dotColor = STATUS_DOT_COLORS[status] || "bg-slate-400";
    const sourceName = language === "cs" ? source_name_cs : source_name_en;
    
    const hasProposedLinks = proposed_authorization_id || proposed_mission_id || proposed_pilot_id;
    const hasConfirmedLinks = confirmed_authorization_id || confirmed_mission_id || confirmed_pilot_id;
    const isExpanded = expandedItems[id];
    
    return (
      <div 
        key={id} 
        className="relative flex gap-4"
        data-testid={`timeline-item-${id}`}
      >
        {/* Timeline line - Language A: thinner connector */}
        {!isLast && (
          <div className="absolute left-[11px] top-8 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />
        )}
        
        {/* Timeline dot - Language A: cleaner dots */}
        <div className={`relative z-10 w-5 h-5 rounded-full ${dotColor} flex items-center justify-center shrink-0 ring-2 ring-white dark:ring-slate-900`}>
          <DirectionIcon className="w-2.5 h-2.5 text-white" />
        </div>
        
        {/* Content */}
        <div className="flex-1 pb-5">
          <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(id)}>
            <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
              {/* Header - always visible */}
              <CollapsibleTrigger asChild>
                <div className="p-3 cursor-pointer hover:bg-muted/30 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <div className={`p-1.5 rounded ${sourceColor}/10`}>
                        <SourceIcon className={`w-4 h-4 ${sourceColor.replace("bg-", "text-")}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-sm leading-tight line-clamp-1">{subject}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {sourceName || (direction === "inbound" ? sender_name || sender_email : t("correspondence.sent"))}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground">{formatTime(received_at)}</span>
                      {needs_review && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Clock className="w-4 h-4 text-amber-500" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t("correspondence.needsReview")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  {/* Quick status badges - visible when collapsed */}
                  {!isExpanded && (hasProposedLinks || hasConfirmedLinks || attachments.length > 0) && (
                    <div className="flex flex-wrap gap-1.5 mt-2 ml-8">
                      {attachments.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <Paperclip className="w-3 h-3 mr-1" />
                          {attachments.length}
                        </Badge>
                      )}
                      {hasConfirmedLinks && (
                        <Badge variant="default" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {t("correspondence.confirmed")}
                        </Badge>
                      )}
                      {hasProposedLinks && !hasConfirmedLinks && (
                        <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {t("correspondence.proposed")}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CollapsibleTrigger>
              
              {/* Expanded content */}
              <CollapsibleContent>
                <div className="px-3 pb-3 border-t border-border/50">
                  {/* Summary */}
                  {(extracted?.summary_cs || extracted?.summary_en) && (
                    <div className="mt-3">
                      <p className="text-sm text-muted-foreground">
                        {language === "cs" ? extracted.summary_cs : extracted.summary_en}
                      </p>
                    </div>
                  )}
                  
                  {/* Extracted data */}
                  {(extracted?.reference_number || extracted?.deadline || extracted?.action_required) && (
                    <div className="mt-3 p-2 bg-muted/30 rounded text-xs space-y-1">
                      <p className="font-medium text-muted-foreground mb-1">{t("correspondence.extractedData")}:</p>
                      {extracted.reference_number && (
                        <p><span className="text-muted-foreground">{t("correspondence.referenceNumber")}:</span> {extracted.reference_number}</p>
                      )}
                      {extracted.deadline && (
                        <p className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-muted-foreground">{t("correspondence.deadline")}:</span> 
                          {new Date(extracted.deadline).toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US")}
                        </p>
                      )}
                      {extracted.action_required && (
                        <p><span className="text-muted-foreground">{t("correspondence.actionRequired")}:</span> {extracted.action_required}</p>
                      )}
                    </div>
                  )}
                  
                  {/* HITL: Links section - CRITICAL */}
                  {(hasConfirmedLinks || hasProposedLinks) && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">{t("correspondence.linkedTo")}:</p>
                      
                      {/* Confirmed links */}
                      {confirmed_authorization_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/30">
                            <Shield className="w-3 h-3 mr-1" />
                            {t("correspondence.authorization")}
                          </Badge>
                          <span className="text-green-600">{t("correspondence.confirmedBadge")}</span>
                        </div>
                      )}
                      {confirmed_mission_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/30">
                            <Plane className="w-3 h-3 mr-1" />
                            {t("correspondence.mission")}
                          </Badge>
                          <span className="text-green-600">{t("correspondence.confirmedBadge")}</span>
                        </div>
                      )}
                      {confirmed_pilot_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/30">
                            <User className="w-3 h-3 mr-1" />
                            {t("correspondence.pilot")}
                          </Badge>
                          <span className="text-green-600">{t("correspondence.confirmedBadge")}</span>
                        </div>
                      )}
                      
                      {/* Proposed links - CLEARLY MARKED AS PENDING VERIFICATION */}
                      {proposed_authorization_id && !confirmed_authorization_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t("correspondence.authorization")}
                          </Badge>
                          <span className="text-amber-600">{t("correspondence.proposedBadge")}</span>
                        </div>
                      )}
                      {proposed_mission_id && !confirmed_mission_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t("correspondence.mission")}
                          </Badge>
                          <span className="text-amber-600">{t("correspondence.proposedBadge")}</span>
                        </div>
                      )}
                      {proposed_pilot_id && !confirmed_pilot_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t("correspondence.pilot")}
                          </Badge>
                          <span className="text-amber-600">{t("correspondence.proposedBadge")}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Attachments */}
                  {attachments.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        {t("correspondence.attachments")} ({attachments.length}):
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {attachments.map((att, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            <Paperclip className="w-3 h-3 mr-1" />
                            {att.filename || `${t("correspondence.attachment")} ${idx + 1}`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-3 flex items-center justify-between">
                    {needs_review && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                        <Clock className="w-3 h-3 mr-1" />
                        {t("correspondence.reviewInQueue")}
                      </Badge>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs ml-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDetails?.(item);
                      }}
                      data-testid={`timeline-view-details-${id}`}
                    >
                      {t("correspondence.viewEmail")}
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>
      </div>
    );
  };
  
  return (
    <div className="relative" data-testid="correspondence-timeline">
      {/* Render grouped by date */}
      {groups ? (
        Object.entries(groups).map(([dateLabel, items]) => (
          <div key={dateLabel} className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-muted-foreground">{dateLabel}</h3>
              <div className="flex-1 h-px bg-border" />
            </div>
            {items.map((item, idx) => renderTimelineItem(item, idx === items.length - 1))}
          </div>
        ))
      ) : (
        // Render flat list
        visibleCorrespondence.map((item, idx) => renderTimelineItem(item, idx === visibleCorrespondence.length - 1))
      )}
      
      {/* Load more button */}
      {showLoadMore && hasMore && (
        <div className="flex justify-center mt-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setVisibleCount(prev => prev + 20)}
            data-testid="timeline-load-more"
          >
            {t("common.loading")} ({sortedCorrespondence.length - visibleCount} {language === "cs" ? "dalších" : "more"})
          </Button>
        </div>
      )}
    </div>
  );
};

export default CorrespondenceTimeline;
